import { ChatView } from "@/components/chatview/ChatView";
import { Navbar } from "@/components/layout/Navbar";
import { useChatInitializer, useChat } from "@/hooks";
import { useChatHistory } from "@/hooks/useChatHistory";
import { ChatBottom } from "@/components/chatbottom/ChatBottom";
import { Overview } from "@/components/layout/Overview";
import { Sidebar } from "@/components/sidebar/Sidebar";
import type { Attachment } from "@/types/chat";
import { isImageContentType, isImageFile } from "@/utils/imageFile";
import { ChatProvider } from "@/components/chatview/ChatContext";
import { useState } from "react";
import { Loading } from "@/components/ui/Loading";
import { FilePreviewDialog } from "@/components/chatbottom/FilePreviewDialog";

export const AiChat: React.FC<{ navigate?: (page: string, agentId?: string) => void }> = ({ navigate }) => {
  const { chatId, initialMessages } = useChatInitializer();
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const {
    handleLoadMore,
    handleLoadHistory,
    isLoading: isHistoryLoading,
  } = useChatHistory();

  const {
    messages,
    input,
    setInput,
    handleSubmit,
    isLoading,
    stop,
    append,
    setMessages,
    streamingMessageId,
    setStreamingMessageId,
  } = useChat({
    initialMessages,
  });

  const handleClearMessages = () => {
    setMessages([]);
  };

  const handleSendMessage = async (message: string) => {
    if (message.trim() && append) {
      await append({ content: message, role: "user" });
    }
  };

  const [previewFile, setPreviewFile] = useState<{
    url: string;
    name: string;
    contentType?: string;
  } | null>(null);

  const handlePreviewFile = (file: {
    url?: string;
    name?: string;
    contentType?: string;
  }) => {
    if (file.url) {
      setPreviewFile({
        url: file.url,
        name: file.name || "文件预览",
        contentType: file.contentType,
      });
    }
  };

  return (
    <div className="h-full w-screen flex overflow-hidden bg-background">
      <ChatProvider
        append={append}
        setMessages={setMessages}
        setStreamingMessageId={setStreamingMessageId}
        previewFile={handlePreviewFile}
      >
        <Sidebar
          onLoadHistory={handleLoadHistory}
          onClearMessages={handleClearMessages}
          mode="desktop"
          onNavigate={navigate}
        />

        <div className="flex-1 flex flex-col h-full min-w-0 overflow-hidden relative">
          <div className="w-full shrink-0">
            <Navbar
              onLoadHistory={handleLoadHistory}
              onClearMessages={handleClearMessages}
              onNavigate={navigate}
            />
          </div>

          <div className="flex-1 overflow-hidden relative w-full">
            {isHistoryLoading ? (
              <div className="h-full w-full flex items-center justify-center">
                <Loading size={40} />
              </div>
            ) : messages.length === 0 ? (
              <div className="h-full w-full flex items-center justify-center pt-20">
                <Overview onSendMessage={handleSendMessage} />
              </div>
            ) : (
              <div className="h-full w-full overflow-hidden px-2">
                <div className="w-full max-w-[65rem] mx-auto h-full pt-12">
                  <ChatView
                    id={chatId}
                    messages={messages}
                    streamingMessageId={streamingMessageId}
                    onLoadMore={handleLoadMore}
                  />
                </div>
              </div>
            )}
          </div>
          {!isHistoryLoading && (
            <div className="w-full shrink-0 max-w-[65rem] mx-auto">
              <ChatBottom 
                input={input}
                setInput={setInput}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
                stop={stop}
                attachments={attachments}
                setAttachments={setAttachments}
                hasMessages={messages.length > 0}
                append={append}
              />
            </div>
          )}
        </div>
        <FilePreviewDialog
          isOpen={!!previewFile}
          onOpenChange={(open) => !open && setPreviewFile(null)}
          previewItem={previewFile}
          previewType={
            isImageContentType(previewFile?.contentType) ||
            isImageFile(previewFile?.name || previewFile?.url || "")
              ? "image"
              : "other"
          }
        />
      </ChatProvider>
    </div>
  );
};
